import { FlexRow } from '@/components/common/Flex';
import { AlertCircle } from '@/components/common/icons';
import BasicModalLayout from '@/components/common/modals/BasicModalLayout';
import Modal from '@/components/common/modals/Modal';
import { Paragraph } from '@/components/common/Typography';
import { startFinancialEntityAddFlow } from '@/components/financialEntities/FinancialEntityAddFlow';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { getConnectedInstitutionText } from '@common/react/accountLinking';
import { useInstitutions } from '@common/react/financialEntities';
import { Button } from '../common/Button';
import { FlexCol } from '../common/Flex';

interface InstitutionAlreadyConnectedModalProps {
    institutionName: string;
}

export const showInstitutionAlreadyConnectedModal = (
    props: InstitutionAlreadyConnectedModalProps
) => ModalManager.show(InstitutionAlreadyConnectedModal, props);

const InstitutionAlreadyConnectedModal =
    ModalManager.create<InstitutionAlreadyConnectedModalProps>(
        ({ institutionName }) => {
            useTrackModalShown('Institution Already Connected', {
                institutionName,
            });

            const modal = useModal();
            const institutions = useInstitutions();
            const connectedInstitution = institutions.find(
                (i) => i.name === institutionName
            );

            const connectedInstitutionText =
                connectedInstitution &&
                getConnectedInstitutionText(connectedInstitution, 'WEB');

            return (
                <Modal isCentered isOpen={modal.visible} onClose={modal.remove}>
                    <BasicModalLayout
                        minH="200px"
                        title={
                            <FlexRow gap={2} align="center">
                                <AlertCircle color="red.300" />
                                Institution already connected
                            </FlexRow>
                        }
                        content={
                            <FlexCol gap={6}>
                                <Paragraph>
                                    You’re already connected to{' '}
                                    {institutionName}.
                                </Paragraph>
                                {connectedInstitutionText && (
                                    <Paragraph>
                                        {connectedInstitutionText}
                                    </Paragraph>
                                )}
                                <Paragraph>
                                    If you need to add more accounts from{' '}
                                    {institutionName}, you can click on that
                                    institution in the &quot;Connected
                                    Institutions&quot; or &quot;Disconnected
                                    Institutions&quot; list during the Add
                                    Account flow.
                                </Paragraph>
                            </FlexCol>
                        }
                        footer={
                            <Button
                                h={10}
                                w={210}
                                onClick={() => {
                                    modal.remove();
                                    startFinancialEntityAddFlow({
                                        initialStep:
                                            'ConnectAccountInstitutionList',
                                    });
                                }}
                                onClickEvent="Update Existing Institution Button Clicked"
                            >
                                Update existing institution
                            </Button>
                        }
                        footerProps={{ mt: 10 }}
                    />
                </Modal>
            );
        }
    );
